/**
 * Advanced Image Lazy Loading Script for Shopify
 * Supports modern browsers with Intersection Observer and fallback for older browsers
 * Handles both img tags and background images with smooth loading animations
 */

class ImageLazyLoader {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px 0px',
      threshold: 0.01,
      loadingClass: 'lazy-loading',
      loadedClass: 'lazy-loaded',
      errorClass: 'lazy-error',
      fadeInDuration: 300,
      retryAttempts: 3,
      retryDelay: 1000,
      ...options
    };

    this.imageObserver = null;
    this.images = [];
    this.retryCount = new WeakMap();
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupCSS();
    this.findImages();
    
    if (this.supportsIntersectionObserver()) {
      this.setupIntersectionObserver();
    } else {
      this.setupScrollListener();
    }

    // Handle dynamic content (for Shopify AJAX)
    this.observeDynamicContent();
  }

  setupCSS() {
    const css = `
      .${this.options.loadingClass} {
        opacity: 0;
        transition: opacity ${this.options.fadeInDuration}ms ease-in-out;
      }
      .${this.options.loadedClass} {
        opacity: 1;
      }
      .${this.options.errorClass} {
        opacity: 0.5;
        filter: grayscale(100%);
      }
      .lazy-bg {
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
      }
    `;

    if (!document.getElementById('lazy-loading-styles')) {
      const style = document.createElement('style');
      style.id = 'lazy-loading-styles';
      style.textContent = css;
      document.head.appendChild(style);
    }
  }

  findImages() {
    // Find img elements with data-src or without loading attribute
    const imgElements = document.querySelectorAll('img[data-src], img:not([loading])');
    
    // Find background image elements
    const bgElements = document.querySelectorAll('[data-bg-src]');
    
    this.images = [...imgElements, ...bgElements];
    
    // Add loading class to all images
    this.images.forEach(img => {
      img.classList.add(this.options.loadingClass);
      
      // For regular img elements without loading attribute, add lazy loading
      if (img.tagName === 'IMG' && !img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }
      
      // For background images, ensure they have the lazy-bg-image class
      if (img.hasAttribute('data-bg-src') && !img.classList.contains('lazy-bg-image')) {
        img.classList.add('lazy-bg-image');
      }
    });
  }

  supportsIntersectionObserver() {
    return 'IntersectionObserver' in window &&
           'IntersectionObserverEntry' in window &&
           'intersectionRatio' in window.IntersectionObserverEntry.prototype;
  }

  setupIntersectionObserver() {
    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.imageObserver.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold
    });

    this.images.forEach(img => this.imageObserver.observe(img));
  }

  setupScrollListener() {
    let ticking = false;
    
    const checkImages = () => {
      this.images = this.images.filter(img => {
        if (this.isInViewport(img)) {
          this.loadImage(img);
          return false; // Remove from array
        }
        return true; // Keep in array
      });
      
      ticking = false;
      
      // Remove listener if no more images
      if (this.images.length === 0) {
        window.removeEventListener('scroll', scrollHandler);
        window.removeEventListener('resize', scrollHandler);
      }
    };

    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(checkImages);
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', scrollHandler, { passive: true });
    
    // Initial check
    checkImages();
  }

  isInViewport(element) {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const threshold = parseInt(this.options.rootMargin);
    
    return (
      rect.top < windowHeight + threshold &&
      rect.bottom > -threshold
    );
  }

  async loadImage(element) {
    try {
      if (element.tagName === 'IMG') {
        await this.loadImgElement(element);
      } else {
        await this.loadBackgroundImage(element);
      }
      
      element.classList.remove(this.options.loadingClass);
      element.classList.add(this.options.loadedClass);
      
      // Dispatch custom event
      element.dispatchEvent(new CustomEvent('lazyLoaded', {
        bubbles: true,
        detail: { element }
      }));
      
    } catch (error) {
      console.warn('Failed to load image:', error);
      this.handleImageError(element);
    }
  }

  loadImgElement(img) {
    return new Promise((resolve, reject) => {
      const src = img.dataset.src || img.src;
      
      if (!src || img.complete) {
        resolve();
        return;
      }

      const tempImg = new Image();
      
      tempImg.onload = () => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }
        resolve();
      };
      
      tempImg.onerror = () => {
        reject(new Error(`Failed to load image: ${src}`));
      };
      
      tempImg.src = src;
    });
  }

  loadBackgroundImage(element) {
    return new Promise((resolve, reject) => {
      const src = element.dataset.bgSrc;
      const srcset = element.dataset.bgSrcset;
      const sizes = element.dataset.bgSizes;
      
      if (!src) {
        resolve();
        return;
      }

      const tempImg = new Image();
      
      tempImg.onload = () => {
        // Set the background image
        element.style.backgroundImage = `url("${src}")`;
        
        // Handle responsive background images if srcset is provided
        if (srcset && sizes) {
          // For modern browsers that support image-set
          const imageSet = srcset.split(',').map(item => {
            const [url, width] = item.trim().split(' ');
            return `url("${url}") ${width}`;
          }).join(', ');
          
          element.style.backgroundImage = `image-set(${imageSet})`;
          
          // Fallback for browsers that don't support image-set
          if (!CSS.supports('background-image', `image-set(${imageSet})`)) {
            element.style.backgroundImage = `url("${src}")`;
          }
        }
        
        // Clean up data attributes
        element.removeAttribute('data-bg-src');
        element.removeAttribute('data-bg-srcset');
        element.removeAttribute('data-bg-sizes');
        
        resolve();
      };
      
      tempImg.onerror = () => {
        reject(new Error(`Failed to load background image: ${src}`));
      };
      
      // Handle responsive images for preloading
      if (srcset) {
        tempImg.srcset = srcset;
        tempImg.sizes = sizes || '100vw';
      }
      
      tempImg.src = src;
    });
  }

  handleImageError(element) {
    const currentRetries = this.retryCount.get(element) || 0;
    
    if (currentRetries < this.options.retryAttempts) {
      this.retryCount.set(element, currentRetries + 1);
      
      setTimeout(() => {
        this.loadImage(element);
      }, this.options.retryDelay * (currentRetries + 1));
    } else {
      element.classList.remove(this.options.loadingClass);
      element.classList.add(this.options.errorClass);
      
      element.dispatchEvent(new CustomEvent('lazyLoadError', {
        bubbles: true,
        detail: { element }
      }));
    }
  }

  observeDynamicContent() {
    // Watch for dynamically added content (common in Shopify with AJAX)
    if ('MutationObserver' in window) {
      const observer = new MutationObserver((mutations) => {
        let shouldRefresh = false;
        
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const hasLazyImages = node.matches('img[data-src], [data-bg-src], img:not([loading])') ||
                                    node.querySelector('img[data-src], [data-bg-src], img:not([loading])');
                if (hasLazyImages) {
                  shouldRefresh = true;
                }
              }
            });
          }
        });
        
        if (shouldRefresh) {
          this.refresh();
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  refresh() {
    // Find new images and observe them
    const currentImages = [...this.images];
    this.findImages();
    
    const newImages = this.images.filter(img => !currentImages.includes(img));
    
    if (this.imageObserver) {
      newImages.forEach(img => this.imageObserver.observe(img));
    }
  }

  destroy() {
    if (this.imageObserver) {
      this.imageObserver.disconnect();
    }
    
    // Remove event listeners
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.scrollHandler);
  }
}

// Auto-initialize when script loads
let lazyLoader;

function initLazyLoading(options = {}) {
  if (lazyLoader) {
    lazyLoader.destroy();
  }
  lazyLoader = new ImageLazyLoader(options);
  return lazyLoader;
}

// Initialize with default options
document.addEventListener('DOMContentLoaded', () => {
  initLazyLoading();
});

// For Shopify theme compatibility
window.ImageLazyLoader = ImageLazyLoader;
window.initLazyLoading = initLazyLoading;

// Handle Shopify section reloads (theme editor)
document.addEventListener('shopify:section:load', () => {
  if (lazyLoader) {
    lazyLoader.refresh();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { ImageLazyLoader, initLazyLoading };
}

